import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { forwardRef, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgMoreActive from 'src/components/Svgs/SvgMoreActive'
import { createSetIsSidenavOpen } from 'src/redux/actions/app.actions'
import {
  selectIsSidenavLockedOpenFromState,
  selectShowSidenavLargeFromState,
} from 'src/redux/selectors/app.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { css } from 'styled-components'

export interface BreadcrumbPathComponent {
  path?: string | undefined
  component?: ReactNode | undefined
  url?: string
}

interface BreadcrumbsTopnavProps {
  breadcrumbs: BreadcrumbPathComponent[]
  actionsComponent?: React.ReactElement
  hideShadow?: boolean
  analytics?: string
}

const BreadcrumbsTopnav = forwardRef<HTMLDivElement, BreadcrumbsTopnavProps>(
  (props, ref): React.ReactElement => {
    const dispatch = useDispatch()
    const fixedElementPushdown = useSelector(selectFixedElementPushdown)
    const showSidenavLarge = useSelector(selectShowSidenavLargeFromState)
    const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)
    const rightShift = sideNavLockedOpen ? '208px' : '40px'
    const history = useHistory()
    const { analytics } = useAnalyticsContext()

    const onClick = (): void => {
      dispatch(createSetIsSidenavOpen(true))
    }

    const handlePathClick = (url?: string): void => {
      if (url !== undefined) {
        history.replace(url)
      }
      if (props.analytics !== undefined) {
        analytics.track(props.analytics)
      }
    }

    const renderPath = (crumb: BreadcrumbPathComponent): ReactNode => {
      return (
        <>
          {renderLink(
            crumb,
            <>
              {crumb.component !== undefined && <div>{crumb.component}</div>}
              {crumb.component === undefined && (
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {crumb.path}
                </span>
              )}
            </>,
          )}
          <span
            css={css`
              margin: 0 10px;
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {'/'}
          </span>
        </>
      )
    }

    const renderTerminus = (crumb: BreadcrumbPathComponent): ReactNode => {
      return (
        <div
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )};
            display: flex;
            gap: 4px;
            justify-content: center;
            align-items: center;
            white-space: normal;
            padding-bottom: 5px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              padding-bottom: 0px;
            `)}
          `}
        >
          {crumb.component !== undefined && <div>{crumb.component}</div>}
          {crumb.component === undefined && (
            <span
              css={css`
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {crumb.path}
            </span>
          )}
        </div>
      )
    }

    const renderLink = (
      crumb: BreadcrumbPathComponent,
      children: ReactNode,
    ): ReactNode => {
      return (
        <HoverableLink
          onClick={(): void => handlePathClick(crumb.url)}
          ariaLabel={crumb.path ?? crumb.url ?? 'navigate'}
          colorSvgOnHover
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )};
            display: flex;
            gap: 4px;
            justify-content: center;
            align-items: center;
            white-space: normal;
            padding-bottom: 5px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              padding-bottom: 0px;
            `)}
          `}
        >
          {children}
        </HoverableLink>
      )
    }

    return (
      <>
        <div
          css={css`
            top: ${fixedElementPushdown + 72}px;
            z-index: 1;
            position: fixed;
            right: 0;
            height: 80px;
            width: calc(100% - ${rightShift});
            box-shadow: ${props.hideShadow
              ? 0
              : `0 0 10px 0 rgba(0, 0, 0, 0.15)`};
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              height: 58px;
              width: 100%;
            `)}
          `}
        />
        <div
          css={css`
            height: 80px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              height: 58px;
            `)}
          `}
        />
        <div
          ref={ref}
          css={css`
            top: ${fixedElementPushdown + 72}px;
            z-index: 4;
            position: fixed;
            background-color: ${styleConstants.colors.white};
            border-bottom: ${props.hideShadow
              ? `1px solid ${styleConstants.colors.gray_medium}`
              : 0};
            height: 80px;
            transition: width 200ms ease-out;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition:
              margin 50ms ease-out,
              padding 50ms ease-out,
              border-radius 50ms ease-out,
              width 50ms ease-out;
            width: calc(100% - ${rightShift});
            margin: 0px 80px 0px 0px;
            padding: 18px ${styleConstants.widths.headerPaddingXLGUp};
            border-radius: 0px;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              height: 58px;
              width: 100%;
              margin: 0;
              padding: 0px;
            `)}

            ${getBreakpointOnly(breakpointSizes.SM)(css`
              padding: 18px ${styleConstants.widths.headerPaddingSMOnly};
            `)}

          ${getBreakpointOnly(breakpointSizes.MD)(css`
              padding: 18px ${styleConstants.widths.headerPaddingMDOnly};
            `)}

          ${getBreakpointOnly(breakpointSizes.LG)(css`
              padding: 18px ${styleConstants.widths.headerPaddingLGOnly};
            `)}

          ${!showSidenavLarge
              ? css`
                  width: calc(100% - ${rightShift});
                `
              : css``}
          `}
        >
          <div
            css={css`
              display: none;
              width: 58px;
              height: 58px;
              cursor: pointer;

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                display: flex;
                align-items: center;
                justify-content: center;
              `)}

              ${!showSidenavLarge &&
              css`
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            `}
            onClick={onClick}
            onKeyPress={onClick}
            role="button"
            tabIndex={0}
          >
            <SvgMoreActive />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              min-width: 0;
              width: 100%;
            `}
          >
            {props.breadcrumbs.map((crumb, index) => {
              return (
                <div
                  key={index}
                  css={css`
                    display: flex;
                    min-width: 0;
                    flex-shrink: 2;
                  `}
                >
                  {index < props.breadcrumbs.length - 1 && renderPath(crumb)}
                  {index === props.breadcrumbs.length - 1 &&
                    renderTerminus(crumb)}
                </div>
              )
            })}
            {props.actionsComponent}
          </div>
        </div>
      </>
    )
  },
)

export default BreadcrumbsTopnav
